import { ILeaf, INode } from 'shared/types/tree'
import { deleteLeaf, deleteNode } from '../../TreeActions'
import {
  getItemListExternalIds,
  getPlaceholderAnswerIds,
  getPlaceholderLists,
  getPlaceholderTemplate,
  getPlaceholderTemplateIds,
  getQuestionAnswerLists,
  getQuestionItemLists,
  getQuestionTargetLists,
  getTargetListExternalIds,
} from './utility'
import {
  createQuestionAnswerList,
  createQuestionPlaceholder,
} from '../../TreeHelpers'
import { getQuestionPlaceholderNodeUpdates } from './QuestionCleanup'
import { LeafTypes, NodeBodyKeys, NodeType } from 'shared/types/ingestion'
import { nanoid } from 'nanoid'
import { leafOfType } from '../../../utility/TreeHelpers'
import {
  appendNodeToTree,
  createLeaf,
  retrieveIds,
  LeafCreateParams,
  LeafRequestParams,
  updateLeafData,
  updateTreeNode,
} from '../../TreeRequests'

async function asynchronouslyDeleteNodes(...nodes: INode[]) {
  return Promise.all(
    nodes.map((node) => {
      return deleteNode({
        treeId: node.tree_id,
        versionId: node.version,
        nodeId: node.id,
      }).catch((_e) => {
        // usually means already deleted
      })
    }),
  )
}

async function appendQuestionAnswersList(node: INode) {
  const params = {
    ...retrieveIds(node),
    ...createQuestionAnswerList(),
  }
  return appendNodeToTree(params)
}

export async function deleteExtraOrAddMissingQuestionAnswerList(
  question: INode,
) {
  const [first, ...others] = getQuestionAnswerLists(question)
  await asynchronouslyDeleteNodes(...others)
  if (!first) {
    await appendQuestionAnswersList(question)
  }
}

export async function deletePlaceholderWithoutContent(question: INode) {
  if (
    getPlaceholderTemplate(question) ||
    getPlaceholderLists(question).length > 0
  ) {
    throw new Error('trying to delete placeholder with content')
  }
  return asynchronouslyDeleteNodes(question)
}

export async function fixPlaceholderListIssue(question: INode) {
  const templateIds = getPlaceholderTemplateIds(question)
  const placeholderLists = getPlaceholderLists(question)
  const goodOne = placeholderLists.find((list) => {
    const listIds = list.nodes.map((n) => n.body?.ExternalID)
    // the list is good if there isn't a template id that's not in the list
    return !templateIds?.find((id) => !listIds.includes(id))
  })
  if (!goodOne) {
    throw new Error('didnt find one')
  }
  const toDelete = placeholderLists.filter((list) => list.id !== goodOne.id)
  question.nodes = question.nodes.filter((n) => !toDelete.includes(n))
  return asynchronouslyDeleteNodes(...toDelete)
}

export function changePlaceholderExternalIds(question: INode) {
  const { toChangeExternalId } = getQuestionPlaceholderNodeUpdates(question)
  return toChangeExternalId.reduce((acc, node) => {
    return acc.then(async () => {
      if (!node.body) node.body = {}
      node.body[NodeBodyKeys.ExternalID] = nanoid(10)
      await updateTreeNode({
        ...retrieveIds(node),
        body: node.body,
      })
    })
  }, Promise.resolve())
}

// This adds in the missing placeholder target nodes, they will not
// have an answer associated with them, if there was one it is now lost
export async function fixPlaceholderMissingTargets(question: INode) {
  const templateIds = getPlaceholderTemplateIds(question)
  const answerIds = getPlaceholderAnswerIds(question)
  if (!templateIds) {
    throw new Error(
      'we should not be trying to fix in this way when template is missing',
    )
  }
  const missing = templateIds.filter((id) => !answerIds?.includes(id))
  if (!missing.length) return Promise.resolve()
  const [placeholderList] = getPlaceholderLists(question)

  return missing.reduce((acc: Promise<unknown>, externalId) => {
    return acc.then(() => {
      const placeholderNode = createQuestionPlaceholder({
        externalId,
        value: '',
      })
      return appendNodeToTree({
        ...retrieveIds(placeholderList),
        ...placeholderNode,
      })
    })
  }, Promise.resolve())
}

export async function fixTargetsMissingExternalIds(question: INode) {
  const [targetList] = getQuestionTargetLists(question)
  const targets = getTargetListExternalIds(targetList)
  if (targets.length === targetList.nodes.length) return Promise.resolve()
  const [itemList] = getQuestionItemLists(question)
  const itemExternalIds = getItemListExternalIds(itemList)
  if (itemExternalIds.length === targetList.nodes.length) {
    //  here we had the external ids associated with the target nodes but lost them
    //  we'll try to add them back in in order
    const missingIds = itemExternalIds.filter((id) => !targets.includes(id))
    if (missingIds.length + targets.length !== itemExternalIds.length) {
      throw new Error('that was unexpected')
    }
    const targetsWithoutIds = targetList.nodes.filter(
      (n) => !n.body?.ExternalID,
    )
    if (targetsWithoutIds.length !== missingIds.length) {
      throw new Error('that was a different unexpected')
    }
    return missingIds.reduce((acc, missingId, i) => {
      return acc.then(async () => {
        const toUpdate = targetsWithoutIds[i]
        const newBody = {
          [NodeBodyKeys.ExternalID]: missingId,
          [NodeBodyKeys.StudentFacing]: true,
        }
        await updateTreeNode({
          ...retrieveIds(toUpdate),
          type: NodeType.QuestionTarget,
          body: newBody,
          hasAnswer: true,
        })
      })
    }, Promise.resolve())
  }
  throw new Error('not sure how to handle this yet')
}

export async function setPointsValue(question: INode) {
  const leaf = leafOfType(question, LeafTypes.Points)
  if (!leaf) {
    const params: LeafCreateParams = {
      tree_id: question.tree_id,
      version: question.version,
      id: question.id,
      leaves: [
        {
          type: LeafTypes.Points,
          body: {
            value: 4,
          },
        },
      ],
    }
    return createLeaf(params)
  }

  const params: LeafRequestParams = {
    tree_id: question.tree_id,
    version: question.version,
    id: leaf?.id || question.id,
    body: { body: { value: 4 } },
  }
  return updateLeafData(params)
}

export async function deleteDuplicateLeafTypes(question: INode) {
  const byType =
    question.leaves?.reduce((acc: { [type: string]: ILeaf[] }, leaf) => {
      if (!acc[leaf.type]) acc[leaf.type] = []
      acc[leaf.type].push(leaf)
      return acc
    }, {}) || {}
  return Object.entries(byType).reduce((acc, [type, [_leaf, ...others]]) => {
    return acc.then(() => {
      const okToDelete = [
        LeafTypes.Points,
        LeafTypes.Question,
        LeafTypes.Attachment,
        LeafTypes.AnswerEditor,
      ].includes(type as LeafTypes)
      if (!okToDelete) return
      return Promise.all(
        others.map(({ id: leafId, tree_id, version }) =>
          deleteLeaf({ leafId, treeId: tree_id!, versionId: version! }),
        ),
      ).then(() => {})
    })
  }, Promise.resolve())
}
